"use client";

import { useRef, useState, type FormEvent } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { PrivacyAcknowledgement } from "@/components/PrivacyAcknowledgement";
import { Turnstile } from "@/components/Turnstile";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { getLocalContactAnalytics, type LocalContactContext } from "@/lib/localLessons/contactContext";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STUDIO_OPTIONS = [
  "Ely / Soham Studio",
  "Bury St Edmunds Studio",
  "Not sure yet",
] as const;

const QUESTION_OPTIONS = [
  "Lesson choice",
  "Studio question",
  "Nervous beginner",
  "Booking question",
  "Other",
] as const;

const fieldClass =
  "mt-2 w-full rounded-[14px] border border-[#dfcfb3] bg-white/82 px-4 py-3 text-sm text-[#113b35] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] placeholder:text-[#87918c] outline-none transition focus:border-[#d5a746] focus:ring-2 focus:ring-[#d5a746]/24";

type LocalContactFormProps = {
  siteKey: string;
  initialContext?: LocalContactContext;
  bookingEnquiry?: { name: string; email: string; phone: string; message: string };
};

export function LocalContactForm({ siteKey, initialContext, bookingEnquiry }: LocalContactFormProps) {
  const pagePath = bookingEnquiry ? "/local-lessons" : "/contact";
  const submitLabel = bookingEnquiry ? "Ask about another time" : "Send message";
  const submittingRef = useRef(false);
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const [name, setName] = useState(bookingEnquiry?.name ?? "");
  const [email, setEmail] = useState(bookingEnquiry?.email ?? "");
  const [phone, setPhone] = useState(bookingEnquiry?.phone ?? "");
  const [preferredStudio, setPreferredStudio] = useState(initialContext?.preferredStudio ?? "");
  const [questionType, setQuestionType] = useState(initialContext?.questionType ?? "");
  const [message, setMessage] = useState(bookingEnquiry?.message ?? "");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [verificationKey, setVerificationKey] = useState(0);
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const trackEvent = useTrackEvent();
  const trackConfirmedLead = useTrackEvent({ includeAttribution: false });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || status === "success") return;
    setErrorMessage("");

    if (name.trim().length < 2) {
      setStatus("error");
      setErrorMessage("Please add your name so we know who to reply to.");
      return;
    }

    if (!EMAIL_REGEX.test(email.trim())) {
      setStatus("error");
      setErrorMessage("Please add a valid email address so we can reply.");
      return;
    }

    let phoneE164 = "";
    if (phone.trim()) {
      const parsedPhone = parsePhoneNumberFromString(phone.trim(), "GB");
      if (!parsedPhone || !parsedPhone.isValid()) {
        setStatus("error");
        setErrorMessage("Please add a valid phone number, or leave this field blank.");
        return;
      }
      phoneE164 = parsedPhone.number;
    }

    if (!preferredStudio) {
      setStatus("error");
      setErrorMessage("Please choose a studio, or select Not sure yet.");
      return;
    }

    if (!questionType) {
      setStatus("error");
      setErrorMessage("Please choose what you would like help with.");
      return;
    }

    if (message.trim().length < 10) {
      setStatus("error");
      setErrorMessage("Please add a short message so we know how to help.");
      return;
    }

    if (!siteKey) {
      setStatus("error");
      setErrorMessage("Human verification is not available right now. Please try again later.");
      return;
    }

    if (!turnstileToken) {
      setStatus("error");
      setErrorMessage("Please complete the human verification.");
      return;
    }

    trackEvent("local_lesson_enquiry_intent", {
      page_type: bookingEnquiry ? "local_lessons" : "contact",
      source_page: pagePath,
      source_section: bookingEnquiry ? "booking_time_enquiry" : "contact_form",
      source_cta: submitLabel,
      enquiry_reason: questionType,
      preferred_studio: preferredStudio,
      funnel: "local_lessons",
      conversion_intent: "local_lesson_enquiry",
    });

    submittingRef.current = true;
    setStatus("loading");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phoneE164,
          phoneCountry: phoneE164 ? "United Kingdom" : "",
          preferredStudio,
          reason: questionType,
          message: message.trim(),
          source: bookingEnquiry ? "local_lessons_alternative_time" : "contact_page_local_lessons",
          pagePath,
          submissionId,
          turnstileToken,
          companyWebsite,
        }),
      });

      let data: unknown = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok || !data || typeof data !== "object" || !("ok" in data) || data.ok !== true || !("confirmation_state" in data) || data.confirmation_state !== "confirmed") {
        const errorFromResponse =
          data && typeof data === "object" && "error" in data && typeof data.error === "string"
            ? data.error
            : "";
        setTurnstileToken("");
        setVerificationKey((value) => value + 1);
        setStatus("error");
        setErrorMessage(errorFromResponse || "Something went wrong. Please try again.");
        return;
      }

      if ("confirmation_state" in data && data.confirmation_state === "confirmed" && "duplicate" in data && data.duplicate === false) {
        trackConfirmedLead("lead_capture", {
          confirmation_state: "confirmed",
          lead_type: "local_enquiry",
          form_type: "contact",
          source_page: pagePath,
          ...getLocalContactAnalytics(preferredStudio, questionType),
        });
      }
      setSubmissionId(crypto.randomUUID());

      setStatus("success");
      setName("");
      setEmail("");
      setPhone("");
      setPreferredStudio("");
      setQuestionType("");
      setMessage("");
      setTurnstileToken("");
      setCompanyWebsite("");
    } catch {
      setTurnstileToken("");
      setVerificationKey((value) => value + 1);
      setStatus("error");
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <form
      id="contact-form"
      data-clarity-mask={bookingEnquiry ? "true" : undefined}
      onSubmit={handleSubmit}
      className="rounded-[22px] border border-[#dfcfb3] bg-white/74 p-5 shadow-[0_24px_80px_rgba(24,43,37,0.12)] ring-1 ring-white/72 backdrop-blur md:p-6"
    >
      <div className="mb-5 text-center lg:text-left">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#a46f12]">{bookingEnquiry ? "Let’s find a time" : "Send us a message"}</p>
        <h2 className="mt-2 font-serif text-2xl text-[#113b35] md:text-3xl">{bookingEnquiry ? "Talk to the team about another time" : "Ask before you book."}</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#5f6d67]">
          {bookingEnquiry
            ? "Send your preferred dates or times to contact@singingattitude.com. We’ll check what is possible; this request does not reserve a lesson or require payment."
            : "Liuba or the Singing Attitude team usually replies within one working day with a clear next step."}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-semibold text-[#123b35]">
          Your name *
          <input
            type="text"
            autoFocus={Boolean(bookingEnquiry)}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="What should we call you?"
            className={fieldClass}
          />
        </label>

        <label className="text-sm font-semibold text-[#123b35]">
          Email address *
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Where should we reply?"
            className={fieldClass}
          />
        </label>

        <label className="text-sm font-semibold text-[#123b35]">
          Phone (optional)
          <input
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="Only if you prefer a call"
            className={fieldClass}
          />
        </label>

        <label className="text-sm font-semibold text-[#123b35]">
          Preferred studio
          <select
            aria-label="Preferred studio"
            required
            value={preferredStudio}
            onChange={(event) => setPreferredStudio(event.target.value)}
            className={`${fieldClass} appearance-none`}
          >
            <option value="">Choose a studio...</option>
            {STUDIO_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm font-semibold text-[#123b35] md:col-span-2">
          What can we help with?
          <select
            aria-label="What can we help with?"
            required
            value={questionType}
            onChange={(event) => setQuestionType(event.target.value)}
            className={`${fieldClass} appearance-none`}
          >
            <option value="">Choose the closest option...</option>
            {QUESTION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm font-semibold text-[#123b35] md:col-span-2">
          Your message *
          <textarea
            aria-label="Your message *"
            required
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Tell us the one thing you want to check before booking."
            rows={5}
            className={`${fieldClass} min-h-[132px] resize-y`}
          />
        </label>
      </div>

      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={companyWebsite}
        onChange={(event) => setCompanyWebsite(event.target.value)}
        className="hidden"
        aria-hidden="true"
      />

      <div className="mt-5 flex flex-col items-center gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3 text-center lg:text-left">
          {status !== "success" ? <Turnstile
            key={verificationKey}
            siteKey={siteKey}
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken("")}
            onError={() => setTurnstileToken("")}
            size="flexible"
          /> : null}
          <PrivacyAcknowledgement className="max-w-xl text-[#66756f]" />
        </div>
        <button
          type="submit"
          disabled={status === "loading" || status === "success" || !siteKey}
          className="inline-flex min-h-12 w-full items-center justify-center rounded-[12px] bg-[#043f35] px-7 py-3 text-sm font-bold text-white shadow-[0_16px_36px_rgba(4,63,53,0.22)] transition hover:-translate-y-0.5 hover:bg-[#082f29] disabled:cursor-wait disabled:opacity-70 sm:w-auto"
        >
          {status === "loading" ? "Sending..." : submitLabel}
        </button>
      </div>

      {status === "error" && errorMessage ? (
        <p role="alert" className="mt-4 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {errorMessage}
        </p>
      ) : null}
      {status === "success" ? (
        <p role="status" className="mt-4 rounded-[14px] border border-[#d9c89f] bg-[#fff5df] px-4 py-3 text-sm font-semibold text-[#123b35]">
          {bookingEnquiry ? "Thanks. Your request has been received. The team will reply about possible times; no lesson has been reserved or payment taken." : "Thanks. Your message has been sent. We will reply with the clearest next step."}
        </p>
      ) : null}
    </form>
  );
}
